/**
 * News service for TG information system
 * Handles CRUD operations for news and read tracking
 */

import { supabase } from '../lib/supabase'
import type {
  News,
  NewsWithReadStatus,
  CreateNewsData,
  UpdateNewsData,
  NewsReadStats
} from '../types/news'

// ============================================
// News CRUD (Admin)
// ============================================

export async function getNews(includeUnpublished = false): Promise<News[]> {
  let query = supabase
    .from('news')
    .select('*')
    .order('is_pinned', { ascending: false })
    .order('published_at', { ascending: false })

  if (!includeUnpublished) {
    query = query.eq('is_published', true)
  }

  const { data, error } = await query
  if (error) throw error
  return data || []
}

export async function createNews(
  data: CreateNewsData,
  authorName: string
): Promise<News> {
  const { data: { user } } = await supabase.auth.getUser()

  const { data: news, error } = await supabase
    .from('news')
    .insert({
      ...data,
      author_id: user?.id,
      author_name: authorName
    })
    .select()
    .single()

  if (error) throw error
  return news
}

export async function updateNews(
  id: string,
  data: UpdateNewsData
): Promise<News> {
  const { data: news, error } = await supabase
    .from('news')
    .update(data)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return news
}

export async function deleteNews(id: string): Promise<void> {
  const { error } = await supabase
    .from('news')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// ============================================
// News for TG (with read status)
// ============================================

export async function getNewsForSubcontractor(
  subcontractorId: string
): Promise<NewsWithReadStatus[]> {
  // First get all published news
  const { data: news, error: newsError } = await supabase
    .from('news')
    .select('*')
    .eq('is_published', true)
    .order('is_pinned', { ascending: false })
    .order('published_at', { ascending: false })

  if (newsError) throw newsError

  // Then get read status for this subcontractor
  const { data: reads, error: readsError } = await supabase
    .from('news_reads')
    .select('news_id, read_at')
    .eq('subcontractor_id', subcontractorId)

  if (readsError) throw readsError

  const readMap = new Map(reads?.map(r => [r.news_id, r.read_at]) || [])

  return (news || []).map(n => ({
    ...n,
    is_read: readMap.has(n.id),
    read_at: readMap.get(n.id) || null
  }))
}

export async function markNewsAsRead(
  newsId: string,
  subcontractorId: string
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser()

  const { error } = await supabase
    .from('news_reads')
    .upsert({
      news_id: newsId,
      subcontractor_id: subcontractorId,
      user_id: user?.id,
      read_at: new Date().toISOString()
    }, {
      onConflict: 'news_id,subcontractor_id'
    })

  if (error) throw error
}

// ============================================
// News Read Statistics (Admin)
// ============================================

export async function getNewsReadStats(newsId: string): Promise<NewsReadStats> {
  // Get all active non-admin subcontractors
  const { data: subcontractors, error: subError } = await supabase
    .from('subcontractors')
    .select('id, name')
    .eq('is_active', true)
    .eq('is_admin', false)

  if (subError) throw subError

  // Get reads for this news
  const { data: reads, error: readError } = await supabase
    .from('news_reads')
    .select(`
      subcontractor_id,
      read_at,
      subcontractors(name)
    `)
    .eq('news_id', newsId)

  if (readError) throw readError

  const readSubcontractorIds = new Set(reads?.map(r => r.subcontractor_id) || [])
  const totalSubcontractors = subcontractors?.length || 0

  const readers = (reads || []).map(r => {
    const sub = r.subcontractors as { name: string } | { name: string }[] | null
    const name = Array.isArray(sub) ? sub[0]?.name : sub?.name
    return {
      subcontractor_id: r.subcontractor_id,
      subcontractor_name: name || 'Okänd',
      read_at: r.read_at
    }
  })

  const nonReaders = (subcontractors || [])
    .filter(s => !readSubcontractorIds.has(s.id))
    .map(s => ({
      subcontractor_id: s.id,
      subcontractor_name: s.name
    }))

  return {
    news_id: newsId,
    total_subcontractors: totalSubcontractors,
    read_count: readers.length,
    read_percentage: totalSubcontractors > 0
      ? Math.round((readers.length / totalSubcontractors) * 100)
      : 0,
    readers,
    non_readers: nonReaders
  }
}

